/**
 * <hero-slideshow>
 *
 * Dependency-free carousel controller for sections/hero-slideshow.liquid.
 * One instance of this custom element wraps N `.hero-slide` blocks rendered
 * by blocks/slide.liquid via {% content_for 'blocks' %}.
 *
 * Responsibilities (deliberately just the shared shell — every slide's own
 * content/media/animation choices live in the slide block's own markup/CSS):
 *   - autoplay with pause-on-hover, pause-when-offscreen, pause-when-tab-hidden
 *   - touch/pointer swipe (passive listeners, no scroll-jank)
 *   - keyboard arrow navigation
 *   - progress-bar / dot / number / thumbnail pagination sync
 *   - background-video play/pause tied to the active slide
 *   - prefers-reduced-motion: disables autoplay + background video autoplay,
 *     collapses transition duration to ~0
 *   - suppresses the click on a slide's whole-slide link (blocks/slide.liquid
 *     `slide_link`) when that click was actually the tail end of a swipe past
 *     the drag threshold, so dragging never accidentally navigates
 *
 * No dependency on the theme's internal `@theme/component` base class, so
 * this file is portable to any Online Store 2.0 theme.
 */

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

class HeroSlideshow extends HTMLElement {
  /** @type {HTMLElement[]} */
  slides = [];
  /** @type {HTMLButtonElement[]} */
  pageButtons = [];
  index = 0;
  #rafId = null;
  #elapsed = 0;
  #lastTick = 0;
  #paused = false;
  #pointerStartX = 0;
  #pointerStartY = 0;
  #pointerDeltaX = 0;
  #pointerActive = false;
  #suppressNextClick = false;
  #io = null;
  #visible = true;

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll(':scope [data-hero-slide]'));
    this.pageButtons = Array.from(this.querySelectorAll(':scope [data-hero-page]'));
    this.prevButton = this.querySelector(':scope [data-hero-prev]');
    this.nextButton = this.querySelector(':scope [data-hero-next]');
    this.progressFill = this.querySelector(':scope [data-hero-progress]');
    this.liveRegion = this.querySelector(':scope [data-hero-live]');
    this.viewport = this.querySelector(':scope [data-hero-viewport]');

    if (this.slides.length === 0) return;

    this.autoplay = this.dataset.autoplay === 'true';
    this.autoplayDelay = Number(this.dataset.autoplayDelay) || 5000;
    this.loop = this.dataset.loop !== 'false';
    this.pauseOnHover = this.dataset.pauseOnHover !== 'false';
    this.enableSwipe = this.dataset.enableSwipe !== 'false';
    this.enableKeyboard = this.dataset.enableKeyboard !== 'false';
    this.reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY).matches;

    // A single slide: nothing to control, just show it and stop.
    if (this.slides.length === 1) {
      this.slides[0].dataset.active = 'true';
      return;
    }

    this.#goTo(0, { announce: false, userInitiated: false });

    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.#step(-1));
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.#step(1));
    }
    this.pageButtons.forEach((button, i) => {
      button.addEventListener('click', () => this.#goTo(i, { announce: true, userInitiated: true }));
    });

    if (this.pauseOnHover) {
      this.addEventListener('mouseenter', () => this.#setPaused(true));
      this.addEventListener('mouseleave', () => this.#setPaused(false));
      this.addEventListener('focusin', () => this.#setPaused(true));
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.#setPaused(false);
      });
    }

    document.addEventListener('visibilitychange', () => {
      this.#setPaused(document.hidden || (this.pauseOnHover && this.matches(':hover')));
    });

    if ('IntersectionObserver' in window) {
      this.#io = new IntersectionObserver(
        (entries) => {
          this.#visible = entries[0]?.isIntersecting ?? true;
          this.#setPaused(!this.#visible || document.hidden);
        },
        { threshold: 0.2 }
      );
      this.#io.observe(this);
    }

    if (this.enableKeyboard) {
      window.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        const withinFocus = this.contains(document.activeElement) && document.activeElement !== document.body;
        if (!withinFocus && !this.matches(':hover')) return;
        event.preventDefault();
        this.#step(event.key === 'ArrowLeft' ? -1 : 1);
      });
    }

    if (this.enableSwipe) {
      if (this.viewport) this.viewport.style.touchAction = 'pan-y';
      this.addEventListener('pointerdown', this.#onPointerDown, { passive: true });
      this.addEventListener('pointermove', this.#onPointerMove, { passive: true });
      this.addEventListener('pointerup', this.#onPointerUp, { passive: true });
      this.addEventListener('pointercancel', this.#onPointerUp, { passive: true });
    }

    // A slide's background link (blocks/slide.liquid `slide_link`) sits under
    // the whole slide. Without this, dragging past the swipe threshold on a
    // pointer device that ends the drag over the link can still fire a click
    // and navigate away mid-swipe. Capture-phase click veto, scoped to just
    // the swipe that triggered it.
    this.addEventListener(
      'click',
      (event) => {
        if (!this.#suppressNextClick) return;
        this.#suppressNextClick = false;
        const link = event.target.closest('.hero-slide__link');
        if (link) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      true
    );

    if (window.matchMedia) {
      window.matchMedia(REDUCED_MOTION_QUERY).addEventListener('change', (event) => {
        this.reducedMotion = event.matches;
      });
    }

    this.#startTimer();
  }

  disconnectedCallback() {
    this.#stopTimer();
    if (this.#io) this.#io.disconnect();
  }

  #onPointerDown = (event) => {
    this.#pointerActive = true;
    this.#pointerStartX = event.clientX;
    this.#pointerStartY = event.clientY;
    this.#pointerDeltaX = 0;
  };

  #onPointerMove = (event) => {
    if (!this.#pointerActive) return;
    this.#pointerDeltaX = event.clientX - this.#pointerStartX;
  };

  #onPointerUp = () => {
    if (!this.#pointerActive) return;
    this.#pointerActive = false;
    const threshold = 48;
    if (Math.abs(this.#pointerDeltaX) > threshold) {
      this.#suppressNextClick = true;
      this.#step(this.#pointerDeltaX < 0 ? 1 : -1);
    }
    this.#pointerDeltaX = 0;
  };

  #step(delta) {
    let next = this.index + delta;
    if (next < 0) next = this.loop ? this.slides.length - 1 : 0;
    if (next >= this.slides.length) next = this.loop ? 0 : this.slides.length - 1;
    this.#goTo(next, { announce: true, userInitiated: true });
  }

  #goTo(nextIndex, { announce, userInitiated }) {
    const previousIndex = this.index;
    const previousSlide = this.slides[previousIndex];
    const nextSlide = this.slides[nextIndex];
    if (!nextSlide) return;

    this.slides.forEach((slide, i) => {
      slide.dataset.active = i === nextIndex ? 'true' : 'false';
      slide.dataset.prev = i === previousIndex && i !== nextIndex ? 'true' : 'false';
      slide.setAttribute('aria-hidden', i === nextIndex ? 'false' : 'true');
    });

    this.pageButtons.forEach((button, i) => {
      button.setAttribute('aria-selected', i === nextIndex ? 'true' : 'false');
    });

    this.#playVideoIfPresent(nextSlide);
    if (previousSlide && previousSlide !== nextSlide) this.#pauseVideoIfPresent(previousSlide);

    this.index = nextIndex;

    if (announce && this.liveRegion) {
      const heading = nextSlide.querySelector('.hero-slide__heading');
      const label = `Slide ${nextIndex + 1} of ${this.slides.length}${heading ? `: ${heading.textContent.trim()}` : ''}`;
      this.liveRegion.textContent = label;
    }

    if (userInitiated) this.#restartTimer();
  }

  #playVideoIfPresent(slide) {
    if (this.reducedMotion) return;
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const video = isMobile
      ? slide.querySelector('[data-hero-video-mobile]') || slide.querySelector('[data-hero-video-desktop]')
      : slide.querySelector('[data-hero-video-desktop]');
    if (!video) return;

    if (!video.dataset.loaded) {
      video.preload = 'auto';
      video.load();
      video.dataset.loaded = 'true';
    }

    const markReady = () => {
      slide.dataset.videoReady = 'true';
    };
    if (video.readyState >= 2) {
      markReady();
    } else {
      video.addEventListener('canplay', markReady, { once: true });
    }

    video.play().catch(() => {
      /* Autoplay can be blocked by the browser; the poster/image stays visible. */
    });
  }

  #pauseVideoIfPresent(slide) {
    slide.querySelectorAll('video').forEach((video) => video.pause());
  }

  #setPaused(paused) {
    this.#paused = paused;
  }

  #startTimer() {
    if (!this.autoplay || this.slides.length < 2) return;
    this.#lastTick = performance.now();
    this.#elapsed = 0;
    this.#rafId = requestAnimationFrame(this.#tick);
  }

  #stopTimer() {
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
    this.#rafId = null;
  }

  #restartTimer() {
    this.#elapsed = 0;
    this.#lastTick = performance.now();
    if (this.progressFill) this.progressFill.style.width = '0%';
  }

  #tick = (now) => {
    const delta = now - this.#lastTick;
    this.#lastTick = now;

    if (!this.#paused && !this.reducedMotion) {
      this.#elapsed += delta;

      if (this.progressFill) {
        const pct = Math.min(100, (this.#elapsed / this.autoplayDelay) * 100);
        this.progressFill.style.width = `${pct}%`;
      }

      if (this.#elapsed >= this.autoplayDelay) {
        const atEnd = this.index === this.slides.length - 1;
        if (!atEnd || this.loop) {
          this.#step(1);
        } else {
          this.#stopTimer();
          return;
        }
        this.#elapsed = 0;
      }
    }

    this.#rafId = requestAnimationFrame(this.#tick);
  };
}

if (!customElements.get('hero-slideshow')) {
  customElements.define('hero-slideshow', HeroSlideshow);
}
